import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowDown, Loader2 } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { LANDING } from "@/constants/testIds";
import { translations } from "@/i18n/translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = `${process.env.PUBLIC_URL || ""}/logo-somnium.png`;

// ============================================================
// Background — dark moody, evokes private luxury (Somnium feel)
// ============================================================
const HeroBackground = () => (
  <div className="absolute inset-0 overflow-hidden bg-grain bg-vignette" data-testid="landing-hero-bg">
    {/* Base black */}
    <div className="absolute inset-0 bg-black" />
    {/* Layered radial gold highlights */}
    <div
      className="absolute inset-0"
      style={{
        background:
          "radial-gradient(60% 50% at 18% 30%, rgba(212,175,102,0.10) 0%, rgba(0,0,0,0) 60%), " +
          "radial-gradient(50% 45% at 82% 70%, rgba(212,175,102,0.08) 0%, rgba(0,0,0,0) 60%), " +
          "radial-gradient(80% 60% at 50% 100%, rgba(20,20,24,0.9) 0%, rgba(0,0,0,1) 70%)",
      }}
    />
    {/* Subtle vertical sheen */}
    <div
      className="absolute inset-0"
      style={{
        background:
          "linear-gradient(180deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0) 70%, rgba(0,0,0,0.7) 100%)",
      }}
    />
  </div>
);

// ============================================================
// iPhone Mockup with Somnium splash screen on it
// ============================================================
const PhoneMockup = ({ t }) => (
  <div className="relative" data-testid={LANDING.phoneMockup}>
    <div className="phone-halo" aria-hidden />
    <div className="phone-shell">
      <div className="phone-notch" aria-hidden />
      <div className="phone-screen">
        {/* Inner soft glow */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(70% 50% at 50% 45%, rgba(212,175,102,0.10) 0%, rgba(0,0,0,0) 65%)",
          }}
        />
        <div className="relative z-10 flex flex-col items-center justify-center px-6 text-center">
          <div
            className="text-[10px] tracking-luxe uppercase mb-6"
            style={{ color: "rgba(255,255,255,0.55)" }}
          >
            {t.phone.welcome}
          </div>
          <img
            src={LOGO_URL}
            alt="Somnium Group"
            className="w-28 h-28 object-contain mb-6 select-none"
            draggable={false}
          />
          <div className="font-serif-display text-white text-xl md:text-2xl tracking-mid whitespace-nowrap">
            SOMNIUM <span className="opacity-80">GROUP</span>
          </div>
          <div
            className="mt-6 text-[10px] tracking-luxe uppercase"
            style={{ color: "rgba(212,175,102,0.85)" }}
          >
            {t.phone.access}
          </div>
          <div
            className="mt-12 text-[10px] tracking-mid"
            style={{ color: "rgba(255,255,255,0.35)" }}
          >
            {t.phone.tag}
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ============================================================
// Hero (Alfred-style intro animation)
// ============================================================
const Hero = ({ t, onLangToggle, locale, scrollToForm }) => {
  const reduce = useReducedMotion();
  const [stage, setStage] = useState(reduce ? 2 : 0);
  // 0 = splash logo, 1 = expand, 2 = full hero

  useEffect(() => {
    if (reduce) return;
    const t1 = setTimeout(() => setStage(1), 1200);
    const t2 = setTimeout(() => setStage(2), 2200);
    // Safety net: no matter what, always reveal the hero within 3.5s
    const safety = setTimeout(() => setStage(2), 3500);
    // Reveal instantly on any interaction (click / scroll / touch / keydown)
    const reveal = () => setStage(2);
    window.addEventListener("pointerdown", reveal, { once: true, passive: true });
    window.addEventListener("keydown", reveal, { once: true, passive: true });
    window.addEventListener("wheel", reveal, { once: true, passive: true });
    window.addEventListener("touchstart", reveal, { once: true, passive: true });
    // Reveal when the tab becomes visible again
    const onVis = () => { if (document.visibilityState === "visible") setStage(2); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(safety);
      window.removeEventListener("pointerdown", reveal);
      window.removeEventListener("keydown", reveal);
      window.removeEventListener("wheel", reveal);
      window.removeEventListener("touchstart", reveal);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [reduce]);

  return (
    <section
      data-testid={LANDING.hero}
      className="relative min-h-[100svh] w-full overflow-hidden"
    >
      <HeroBackground />

      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 z-30 flex items-center justify-between px-6 md:px-12 py-6">
        <div className="flex items-center gap-3">
          <img
            src={LOGO_URL}
            alt="Somnium Group"
            className="w-8 h-8 object-contain opacity-90"
            draggable={false}
          />
          <span
            className="font-serif-display text-white text-sm tracking-mid hidden sm:block"
            data-testid={LANDING.wordmark}
          >
            SOMNIUM GROUP
          </span>
        </div>
        <button
          type="button"
          onClick={onLangToggle}
          data-testid={LANDING.langToggle}
          className="text-white/70 hover:text-white text-xs tracking-luxe uppercase transition-colors"
          aria-label="Toggle language"
        >
          {locale === "fr" ? "EN" : "FR"}
        </button>
      </div>

      {/* Splash overlay */}
      <AnimatePresence>
        {stage < 2 && (
          <motion.div
            initial={{ opacity: 1 }}
            animate={{ opacity: stage === 1 ? 0 : 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 z-40 flex items-center justify-center bg-black"
          >
            <div className="flex flex-col items-center">
              <motion.img
                src={LOGO_URL}
                alt="Somnium Group"
                draggable={false}
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.0, ease: "easeOut" }}
                className="w-24 h-24 md:w-32 md:h-32 object-contain"
              />
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.4 }}
                className="mt-6 font-serif-display text-white text-3xl md:text-4xl tracking-mid"
              >
                SOMNIUM <span className="opacity-70">GROUP</span>
              </motion.div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.7 }}
                transition={{ duration: 0.9, delay: 0.8 }}
                className="mt-3 text-white/70 text-[11px] md:text-xs tracking-luxe uppercase"
              >
                {t.hero.eyebrow}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero content */}
      <div className="relative z-10 min-h-[100svh] flex flex-col items-center justify-center px-6 md:px-12 pt-24 pb-16">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: stage === 2 ? 1 : 0 }}
          transition={{ duration: 1.2, delay: 0.3, ease: "easeOut" }}
          className="text-center max-w-3xl"
        >
          <div
            className="text-[10px] md:text-xs tracking-luxe uppercase mb-6"
            style={{ color: "rgba(212,175,102,0.85)" }}
            data-testid={LANDING.tagline}
          >
            {t.hero.eyebrow}
          </div>
          <h1 className="font-serif-display text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.1]" style={{ letterSpacing: "0.015em", wordSpacing: "0.04em" }}>
            {t.hero.tagline}
          </h1>
          <p className="mt-6 text-white/65 text-base md:text-lg max-w-xl mx-auto">
            {t.hero.sub}
          </p>
        </motion.div>

        {/* Phone mockup */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.94 }}
          animate={{
            opacity: stage === 2 ? 1 : 0,
            y: stage === 2 ? 0 : 40,
            scale: stage === 2 ? 1 : 0.94,
          }}
          transition={{ duration: 1.4, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12 md:mt-14"
        >
          <PhoneMockup t={t} />
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: stage === 2 ? 1 : 0, y: stage === 2 ? 0 : 20 }}
          transition={{ duration: 0.9, delay: 1.0 }}
          className="mt-12 flex flex-col items-center gap-6"
        >
          <button
            type="button"
            onClick={scrollToForm}
            className="luxe-btn"
            data-testid={LANDING.scrollToForm}
          >
            {t.hero.cta}
          </button>
          <div className="flex flex-col items-center gap-3">
            <span className="text-[10px] tracking-luxe uppercase text-white/40">
              {t.hero.scroll}
            </span>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
              className="text-white/40"
            >
              <ArrowDown size={16} />
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================
// Access Form
// ============================================================
const initialForm = {
  full_name: "",
  email: "",
  linkedin: "",
  profile: "",
  invitation_code: "",
  message: "",
};

const AccessForm = ({ t, locale, sectionRef }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const update = (key) => (e) =>
    setForm((s) => ({ ...s, [key]: e?.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (!form.full_name.trim() || !form.email.trim() || !form.profile) {
      setErrorMsg(t.form.requiredFields);
      toast.error(t.form.requiredFields);
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/access-request`, {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        linkedin: form.linkedin.trim() || null,
        profile: form.profile,
        invitation_code: form.invitation_code.trim() || null,
        message: form.message.trim() || null,
        locale,
      });
      const token = data?.application_token;
      setForm(initialForm);
      if (token) {
        navigate(`/application/${token}?locale=${locale}&fresh=1`);
      }
    } catch (err) {
      const detail =
        err?.response?.data?.detail || t.form.errorGeneric;
      setErrorMsg(detail);
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      ref={sectionRef}
      data-testid={LANDING.formSection}
      className="relative w-full bg-black px-6 md:px-12 py-24 md:py-32 overflow-hidden"
    >
      {/* Subtle background detail */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(60% 40% at 50% 0%, rgba(212,175,102,0.06) 0%, rgba(0,0,0,0) 60%)",
        }}
      />
      <div className="relative max-w-2xl mx-auto">
        <motion.form
              key="form"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6 }}
              onSubmit={handleSubmit}
              data-testid={LANDING.form}
              className="space-y-7"
              noValidate
            >
              <div className="text-center mb-10">
                <div
                  className="text-[10px] tracking-luxe uppercase mb-4"
                  style={{ color: "rgba(212,175,102,0.85)" }}
                >
                  {t.form.eyebrow}
                </div>
                <h2 className="font-serif-display text-white text-3xl sm:text-4xl lg:text-5xl">
                  {t.form.title}
                </h2>
                <p className="mt-5 text-white/65 max-w-xl mx-auto text-base whitespace-pre-line leading-relaxed">
                  {t.form.subtitle}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label
                    htmlFor="full_name"
                    className="text-[10px] tracking-luxe uppercase text-white/55"
                  >
                    {t.form.fullName} *
                  </Label>
                  <Input
                    id="full_name"
                    data-testid={LANDING.fullName}
                    value={form.full_name}
                    onChange={update("full_name")}
                    className="luxe-input h-12 px-4"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="email"
                    className="text-[10px] tracking-luxe uppercase text-white/55"
                  >
                    {t.form.email} *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid={LANDING.email}
                    value={form.email}
                    onChange={update("email")}
                    className="luxe-input h-12 px-4"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="linkedin"
                  className="text-[10px] tracking-luxe uppercase text-white/55"
                >
                  {t.form.linkedin}
                </Label>
                <Input
                  id="linkedin"
                  data-testid={LANDING.linkedin}
                  value={form.linkedin}
                  onChange={update("linkedin")}
                  placeholder="https://linkedin.com/in/…"
                  className="luxe-input h-12 px-4"
                />
                <p className="text-white/40 text-xs">{t.form.linkedinHint}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="text-[10px] tracking-luxe uppercase text-white/55">
                    {t.form.profile} *
                  </Label>
                  <Select
                    value={form.profile}
                    onValueChange={(v) =>
                      setForm((s) => ({ ...s, profile: v }))
                    }
                  >
                    <SelectTrigger
                      data-testid={LANDING.profile}
                      className="luxe-input h-12 px-4"
                    >
                      <SelectValue placeholder={t.form.profilePlaceholder} />
                    </SelectTrigger>
                    <SelectContent className="bg-neutral-950 border-white/10 text-white">
                      {t.form.profileOptions.map((opt) => (
                        <SelectItem
                          key={opt.value}
                          value={opt.value}
                          data-testid={`access-form-profile-option-${opt.value}`}
                        >
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="invitation_code"
                    className="text-[10px] tracking-luxe uppercase text-white/55"
                  >
                    {t.form.invitationCode}
                  </Label>
                  <Input
                    id="invitation_code"
                    data-testid={LANDING.invitationCode}
                    value={form.invitation_code}
                    onChange={update("invitation_code")}
                    className="luxe-input h-12 px-4"
                  />
                  <p className="text-white/40 text-xs">
                    {t.form.invitationHint}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="message"
                  className="text-[10px] tracking-luxe uppercase text-white/55"
                >
                  {t.form.message}
                </Label>
                <Textarea
                  id="message"
                  data-testid={LANDING.message}
                  value={form.message}
                  onChange={update("message")}
                  placeholder={t.form.messagePlaceholder}
                  className="luxe-input p-4 min-h-[120px]"
                />
              </div>

              {errorMsg && (
                <div
                  data-testid={LANDING.errorMessage}
                  className="text-sm text-red-300/90 border border-red-300/20 rounded px-4 py-3 bg-red-950/20"
                >
                  {errorMsg}
                </div>
              )}

              <div className="pt-2 flex flex-col items-center gap-5">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid={LANDING.submit}
                  className="luxe-btn min-w-[260px]"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      {t.form.submitting}
                    </>
                  ) : (
                    t.form.submit
                  )}
                </button>
                <p className="text-white/40 text-xs text-center max-w-md">
                  {t.form.legal}
                </p>
              </div>
            </motion.form>
      </div>
    </section>
  );
};

// ============================================================
// Footer
// ============================================================
const Footer = ({ t }) => (
  <footer className="bg-black border-t border-white/5 px-6 md:px-12 py-10">
    <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <img
          src={LOGO_URL}
          alt="Somnium Group"
          className="w-6 h-6 object-contain opacity-80"
          draggable={false}
        />
        <span className="font-serif-display text-white/80 text-sm tracking-mid">
          SOMNIUM GROUP
        </span>
      </div>
      <div className="text-[10px] tracking-luxe uppercase text-white/40">
        {t.footer.tagline}
      </div>
      <div className="text-white/35 text-xs">{t.footer.copyright}</div>
    </div>
  </footer>
);

// ============================================================
// Landing page
// ============================================================
export default function Landing() {
  const [locale, setLocale] = useState("fr");
  const formRef = useRef(null);
  const t = translations[locale];

  // Ensure document language and title update
  useEffect(() => {
    document.documentElement.lang = locale;
    document.title =
      locale === "fr"
        ? "Somnium Group — Accès privé"
        : "Somnium Group — Private access";
  }, [locale]);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="bg-black text-white">
      <Hero
        t={t}
        locale={locale}
        onLangToggle={() => setLocale(locale === "fr" ? "en" : "fr")}
        scrollToForm={scrollToForm}
      />
      <AccessForm t={t} locale={locale} sectionRef={formRef} />
      <Footer t={t} />
    </main>
  );
}
